import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

// Google renders its own button text, so only app languages Google supports are passed; others use English
const GOOGLE_LOCALES = { en: 'en', am: 'am' };

// Loads the Google Identity Services script once and resolves with window.google.accounts.id
function loadGoogleIdentity() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  return new Promise((resolve, reject) => {
    let script = document.querySelector(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = GOOGLE_SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve(window.google.accounts.id));
    script.addEventListener('error', () => reject(new Error('Google sign-in could not load')));
  });
}

// "Continue with Google" button. Renders nothing when REACT_APP_GOOGLE_CLIENT_ID is unset,
// so the email/password forms are unaffected.
export default function GoogleSignInButton({ onCredential, onError, text = 'continue_with' }) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const { i18n } = useTranslation();
  const locale = GOOGLE_LOCALES[i18n.language] || 'en';
  const ref = useRef(null);
  const callbacks = useRef({ onCredential, onError });
  callbacks.current = { onCredential, onError };

  useEffect(() => {
    if (!clientId || !ref.current) return undefined;
    let cancelled = false;
    loadGoogleIdentity()
      .then((id) => {
        if (cancelled) return;
        id.initialize({
          client_id: clientId,
          callback: ({ credential }) => {
            if (credential) callbacks.current.onCredential(credential);
            else callbacks.current.onError('Google sign-in was not completed. Please try again.');
          },
        });
        id.renderButton(ref.current, { theme: 'outline', size: 'large', text, width: 320, locale });
      })
      .catch(() => {
        if (!cancelled) callbacks.current.onError('Google sign-in is unavailable right now. Please use your password.');
      });
    return () => { cancelled = true; };
  }, [text, clientId, locale]);

  if (!clientId) return null;
  return <div ref={ref} className="flex justify-center" />;
}
